<template>
  <PageSection
    class="training-section-documents"
    :theme="isDark"
  >
    <template #default>
      <div class="container">
        <div
          v-if="title || (documents && documents.length)"
          class="header"
        >
          <div class="column-left">
            <h2
              v-if="title"
              class="title"
              v-inview-animate:once="{name: 'fade-down', duration: 500, delay: 300}"
              v-html="title"
            ></h2>
          </div>
          <div class="column-right desktop-arrows">
            <SliderArrows
              v-if="documents && documents.length && swiperInstance"
              :swiper="swiperInstance"
              :class="{'theme-white': isDark}"
            />
          </div>
          <div class="column-right mobile-arrows">
            <SliderArrows
              v-if="documents && documents.length && mobileSwiperInstance"
              :swiper="mobileSwiperInstance"
              :class="{'theme-white': isDark}"
            />
          </div>
        </div>
        <Swiper
          v-if="documents && documents.length"
          class="documents-slider desktop-slider"
          :slides-per-view="'auto'"
          :watchOverflow="true"
          @swiper="onSwiper"
        >
          <SwiperSlide
            v-for="(doc, index) in documents"
            :key="`doc-${index}`"
          >
            <TrainingSectionDocumentItem
              :document="doc.Document"
              :title="doc.Title"
            />
          </SwiperSlide>
        </Swiper>
        <div
          v-if="documents && documents.length"
          class="documents-list mobile-list"
        >
          <Swiper
            class="mobile-slider"
            :slides-per-view="1"
            :space-between="0"
            :watchOverflow="true"
            @swiper="onMobileSwiper"
          >
            <SwiperSlide
              v-for="(page, pageIndex) in mobileDocumentPages"
              :key="`page-${pageIndex}`"
            >
              <div class="documents-column">
                <div
                  v-for="(doc, index) in page"
                  :key="`doc-mobile-${pageIndex}-${index}`"
                  class="document-item"
                >
                  <TrainingSectionDocumentItem
                    :document="doc.Document"
                    :title="doc.Title"
                        />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </template>
  </PageSection>
</template>

<script>
import PageSection from '@/components/PageSection.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/swiper.scss';
import SliderArrows from '@/components/SliderArrows.vue';
import TrainingSectionDocumentItem from './TrainingSectionDocumentItem.vue';

export default {
  name: 'TrainingSectionDocuments',
  components: {
    PageSection,
    Swiper,
    SwiperSlide,
    SliderArrows,
    TrainingSectionDocumentItem,
  },
  data() {
    return {
      swiperInstance: null,
      mobileSwiperInstance: null,
    };
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    documents: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: String,
      default: 'light',
      validator: (value) => ['light', 'dark'].includes(value),
    },
  },
  computed: {
    isDark() {
      return this.theme === 'dark';
    },
    mobileDocumentPages() {
      const pages = [];
      const docsPerPage = 3;
      for (let i = 0; i < this.documents.length; i += docsPerPage) {
        pages.push(this.documents.slice(i, i + docsPerPage));
      }
      return pages;
    },
  },
  watch: {
    swiperInstance(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
  },
  methods: {
    onSwiper(swiper) {
      this.swiperInstance = swiper;
    },
    onMobileSwiper(swiper) {
      this.mobileSwiperInstance = swiper;
    },
  },
};
</script>

<style lang="scss" scoped>
.training-section-documents {
  @include screen("sm", max) {
    overflow: visible;
  }

  @include screen("sm", min) {
    overflow: hidden;
  }

  @include wRule(
    padding-top,
    (
      xl: 120,
      lg: 100,
      md: 80,
      sm: 80,
    )
  );

  @include wRule(
    padding-bottom,
    (
      xl: 120,
      lg: 100,
      md: 80,
      sm: 80,
    )
  );

  .title {
    display: block;
    width: 100%;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.02em;

    @include wRule(
      font-size,
      (
        xl: 45,
        lg: 35,
        md: 30,
        sm: 45,
      )
    );

    &:not(:last-child) {
      @include wRule(
        margin-bottom,
        (
          xl: 60,
          lg: 50,
          md: 40,
          sm: 50,
        )
      );
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include screen("sm", max) {
      align-items: center;
    }

    &:not(:last-child) {
      @include wRule(
        margin-bottom,
        (
          xl: 180,
          lg: 85,
          md: 130,
          sm: 100,
        )
      );
    }
  }

  .column-left {
    flex: 1;

    @include wRule(
      margin-right,
      (
        xl: 60,
        lg: 40,
        md: 30,
        sm: 20,
      )
    );
  }

  .column-right {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .desktop-arrows {
    @include screen("sm", max) {
      display: none;
    }
  }

  .mobile-arrows {
    display: none;

    @include screen("sm", max) {
      display: flex;
    }
  }

  .desktop-slider {
    @include screen("sm", max) {
      display: none;
    }
  }

  .mobile-list {
    display: none;

    @include screen("sm", max) {
      display: block;
      width: 100%;
    }

    .mobile-slider {
      overflow: hidden;
    }

    .documents-column {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      @include wRule(
        gap,
        (
          xl: 336,
          lg: 190    ,
          md: 220,
          sm: 100,
        )
      );
    }

    .document-item {
      width: 100%;
      display: flex;
      align-items: flex-start;
      gap: 20px;
    }
  }

  .documents-slider {
    overflow: visible;
  }

  .swiper-slide {
    width: auto;
    height: auto;

    @include wRule(
      width,
      (
        xl: 220px,
        lg: 200px,
        md: 191.5px,
        sm: 191.5px,
      )
    );

    @include wRule(
      height,
      (
        xl: 320px,
        lg: 300px,
        md: 280px,
        sm: 280px,
      )
    );

    &:not(:last-child) {
      @include wRule(
        margin-right,
        (
          xl: 188,
          lg: 132,
          md: 113,
          sm: 20,
        )
      );
    }
  }

  .mobile-list {
    .swiper-slide {
      width: 100%;
      height: auto;
      margin-right: 0;
    }
  }
}
</style>
